import React, { useState, useEffect } from 'react';
import { Phone, X, User, Building, Clock, PhoneCall, PhoneMissed } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';

const IncomingCallPopup = ({ call, onAnswer, onReject, onClose }) => {
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (call && call.status === 'answered') {
      const interval = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [call]);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!call) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-5">
      <Card className="w-96 shadow-2xl border-2 border-blue-500">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
              <span className="text-sm font-medium text-gray-600">
                {call.status === 'incoming' ? 'Incoming Call' : call.status === 'answered' ? 'In Call' : 'Call Ended'}
              </span>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Caller Info */}
          <div className="flex items-center space-x-4 mb-6">
            <Avatar className="h-16 w-16">
              <AvatarFallback className="bg-blue-500 text-white text-xl">
                {call.callerName ? call.callerName.substring(0, 2).toUpperCase() : 'UN'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-900">{call.callerName || 'Unknown Caller'}</h3>
              {call.company && (
                <p className="text-sm text-gray-600 flex items-center mt-1">
                  <Building className="w-4 h-4 mr-1" />
                  {call.company}
                </p>
              )}
              <p className="text-sm text-gray-500 mt-1">{call.phoneNumber}</p>
            </div>
          </div>

          {/* Call Duration */}
          {call.status === 'answered' && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg text-center">
              <div className="flex items-center justify-center space-x-2">
                <Clock className="w-4 h-4 text-blue-600" />
                <span className="text-2xl font-mono font-bold text-blue-600">
                  {formatDuration(duration)}
                </span>
              </div>
            </div>
          )}

          {/* Call Actions */}
          {call.status === 'incoming' && (
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={onReject}
                variant="outline"
                className="border-red-500 text-red-600 hover:bg-red-50"
              >
                <PhoneMissed className="w-4 h-4 mr-2" />
                Decline
              </Button>
              <Button
                onClick={onAnswer}
                className="bg-green-500 hover:bg-green-600 text-white"
              >
                <PhoneCall className="w-4 h-4 mr-2" />
                Answer
              </Button>
            </div>
          )}

          {call.status === 'answered' && (
            <Button
              onClick={onReject}
              className="w-full bg-red-500 hover:bg-red-600 text-white"
            >
              <Phone className="w-4 h-4 mr-2" />
              End Call
            </Button>
          )}

          {/* 3CX Integration Note */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              3CX Integration • Extension: {call.extension || 'N/A'}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// 3CX Integration Manager Component
export const use3CXIntegration = () => {
  const [currentCall, setCurrentCall] = useState(null);

  // Mock 3CX webhook listener
  useEffect(() => {
    // This would be replaced with actual 3CX webhook/API integration
    const simulate3CXCall = () => {
      const mockCalls = [
        {
          id: '1',
          callerName: 'Sarah Johnson',
          phoneNumber: '+1 (555) 123-4567',
          company: 'TechCorp Solutions',
          extension: '101',
          status: 'incoming'
        },
        {
          id: '2',
          callerName: 'Michael Chen',
          phoneNumber: '+1 (555) 234-5678',
          company: 'Innovate.io',
          extension: '102',
          status: 'incoming'
        }
      ];

      // Simulate random incoming call every 2 minutes for demo
      const interval = setInterval(() => {
        const randomCall = mockCalls[Math.floor(Math.random() * mockCalls.length)];
        setCurrentCall({ ...randomCall, timestamp: new Date() });
      }, 120000); // 2 minutes

      return () => clearInterval(interval);
    };

    // Uncomment to enable demo calls
    // const cleanup = simulate3CXCall();
    // return cleanup;
  }, []);

  const handleAnswer = () => {
    setCurrentCall(prev => ({ ...prev, status: 'answered' }));
  };

  const handleReject = () => {
    setCurrentCall(prev => ({ ...prev, status: 'ended' }));
    setTimeout(() => setCurrentCall(null), 2000);
  };

  const handleClose = () => {
    setCurrentCall(null);
  };

  // Trigger a test call manually
  const triggerTestCall = () => {
    setCurrentCall({
      id: 'test-' + Date.now(),
      callerName: 'Test Caller',
      phoneNumber: '+1 (555) 999-9999',
      company: 'Test Company',
      extension: '100',
      status: 'incoming',
      timestamp: new Date()
    });
  };

  return {
    currentCall,
    handleAnswer,
    handleReject,
    handleClose,
    triggerTestCall,
    IncomingCallPopup: () => (
      <IncomingCallPopup
        call={currentCall}
        onAnswer={handleAnswer}
        onReject={handleReject}
        onClose={handleClose}
      />
    )
  };
};

export default IncomingCallPopup;